/**
 * Canonical 9-language translations for the research-peptide product-type
 * vocabulary. Used by ProductCard badges and ProductsPage filter chips so
 * every product renders the same localized type label even when the DB
 * record has inconsistent per-product `product_type[lang]` values.
 *
 * Resolves by EXACT match on the canonical English value. If no match
 * (rare custom type), falls back to product.product_type[lang] || .en.
 */
const CANONICAL_PRODUCT_TYPES = {
  Pen: { en: 'Pen', es: 'Pen', ru: 'Ручка', de: 'Pen', fr: 'Stylo',
         it: 'Penna', pl: 'Pen', nl: 'Pen', pt: 'Caneta', au: 'Pen' },
  'Nasal Spray': { en: 'Nasal Spray', es: 'Spray Nasal',
                   ru: 'Назальный Спрей', de: 'Nasenspray',
                   fr: 'Spray Nasal', it: 'Spray Nasale',
                   pl: 'Spray do Nosa', nl: 'Neusspray',
                   pt: 'Spray Nasal', au: 'Nasal Spray' },
  Ampoules: { en: 'Ampoules', es: 'Ampollas', ru: 'Ампулы',
              de: 'Ampullen', fr: 'Ampoules', it: 'Fiale',
              pl: 'Ampułki', nl: 'Ampullen', pt: 'Ampolas',
              au: 'Ampoules' },
  Box: { en: 'Box', es: 'Caja', ru: 'Коробка',
         de: 'Schachtel', fr: 'Boîte', it: 'Confezione',
         pl: 'Pudełko', nl: 'Doos', pt: 'Caixa', au: 'Box' },
  Capsules: { en: 'Capsules', es: 'Cápsulas', ru: 'Капсулы',
              de: 'Kapseln', fr: 'Capsules', it: 'Capsule',
              pl: 'Kapsułki', nl: 'Capsules', pt: 'Cápsulas',
              au: 'Capsules' },
  'IV Kit': { en: 'IV Kit', es: 'Kit IV', ru: 'Набор IV',
              de: 'IV-Set', fr: 'Kit IV', it: 'Kit IV',
              pl: 'Zestaw IV', nl: 'IV-Set', pt: 'Kit IV',
              au: 'IV Kit' },
  Lyophilisate: { en: 'Lyophilisate', es: 'Liofilizado',
                  ru: 'Лиофилизат', de: 'Lyophilisat',
                  fr: 'Lyophilisat', it: 'Liofilizzato',
                  pl: 'Liofilizat', nl: 'Lyofilisaat',
                  pt: 'Liofilizado', au: 'Lyophilisate' },
  Vial: { en: 'Vial', es: 'Vial', ru: 'Флакон',
          de: 'Fläschchen', fr: 'Flacon', it: 'Flaconcino',
          pl: 'Fiolka', nl: 'Flacon', pt: 'Frasco', au: 'Vial' },
  Sachets: { en: 'Sachets', es: 'Sobres', ru: 'Саше',
             de: 'Beutel', fr: 'Sachets', it: 'Bustine',
             pl: 'Saszetki', nl: 'Sachets', pt: 'Sachês',
             au: 'Sachets' },
  // Generic catch-alls that some DB records use instead of a concrete format.
  'Research Compound': { en: 'Research Compound', es: 'Compuesto de investigación',
                         ru: 'Исследовательский комплекс',
                         de: 'Forschungsverbindung', fr: 'Composé de recherche',
                         it: 'Composto di ricerca', pl: 'Związek badawczy',
                         nl: 'Onderzoekspeptide', pt: 'Composto de pesquisa',
                         au: 'Research Compound' },
  'Research Peptide': { en: 'Research Peptide', es: 'Péptido de investigación',
                        ru: 'Исследовательский пептид',
                        de: 'Forschungspeptid', fr: 'Peptide de recherche',
                        it: 'Peptide di ricerca', pl: 'Peptyd badawczy',
                        nl: 'Onderzoekspeptide', pt: 'Peptídeo de pesquisa',
                        au: 'Research Peptide' },
  'Research Pen': { en: 'Research Pen', es: 'Pen de investigación',
                    ru: 'Исследовательская ручка',
                    de: 'Forschungs-Pen', fr: 'Stylo de recherche',
                    it: 'Penna di ricerca', pl: 'Pen badawczy',
                    nl: 'Onderzoeks-Pen', pt: 'Caneta de pesquisa',
                    au: 'Research Pen' },
};

/**
 * Resolve a product's type label in the active locale, preferring the
 * canonical translation when the EN key is in our vocabulary.
 */
export const getLocalizedProductType = (productType, currentLanguage = 'en') => {
  if (!productType) return '';
  if (typeof productType === 'string') return productType;
  const en = productType.en;
  const canonical = en && CANONICAL_PRODUCT_TYPES[en];
  if (canonical && canonical[currentLanguage]) return canonical[currentLanguage];
  return productType[currentLanguage] || productType.en || '';
};

export default CANONICAL_PRODUCT_TYPES;
